import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import type { Any } from '@api/orvalFetcher'

/**
 * Serializable socket config kept in Redux.
 * NOTE: do NOT store functions or Socket instances here.
 * Store only the minimal serializable info (name, match string, url, delay).
 */
export type SocketConfigLite = {
  name: string
  // string patterns as "/tasks" or ["/tasks", "/projects"]
  match: Array<string>
  url?: string
  options?: Record<string, Any>
}

/** Serializable connection metadata for a named socket. */
type SocketConn = {
  connected: boolean
  // lastPath?: string
}

type SocketsState = {
  configs: Array<SocketConfigLite> // what sockets we know about
  connections: Record<string, SocketConn> // status per socket
  currentPath?: string // latest pathname (managed by bridge)
}

const initialState: SocketsState = {
  configs: [],
  connections: {},
  currentPath: undefined,
}

const socketsSlice = createSlice({
  name: 'sockets',
  initialState,
  reducers: {
    /**
     * Initialize the list of socket configs (serializable only).
     * Call this once on app startup (bridge will do it for you).
     */
    initConfigs(state, action: PayloadAction<Array<SocketConfigLite>>) {
      state.configs = action.payload
      for (const { name } of action.payload) {
        state.connections[name] = state.connections[name] ?? { connected: false }
      }
    },

    /**
     * Update current path. The middleware / bridge will dispatch this when
     * the route changes so sockets can autoconnect/disconnect.
     */
    setCurrentPath(state, action: PayloadAction<string>) {
      state.currentPath = action.payload
    },

    /**
     * Update connected flag for a named socket.
     * The socketService dispatches this when sockets connect/disconnect.
     */
    setConnected(state, action: PayloadAction<{ name: string; connected: boolean }>) {
      const { name, connected } = action.payload
      state.connections[name] = state.connections[name] ?? { connected: false }
      state.connections[name].connected = connected
    },
  },
})

export const { initConfigs, setCurrentPath, setConnected } = socketsSlice.actions

export const socketsReducer = socketsSlice.reducer
